import { parseRichDetails } from "../../shared/document-line-content";
import { parseDocumentTaxes } from "../../shared/document-taxes";
import type { DraftForm } from "./InvoiceDocument";

export interface PendingDraftSave { key: string; path: string; method: "POST" | "PATCH"; body: string; form: DraftForm }
export interface DraftRecovery {
  schema: 1; editor: string; updatedAt: number; documentId: string | null; baseVersion: number | null;
  form: DraftForm; pending: PendingDraftSave | null;
}
export const recoveryPrefix = "signalbill:draft-recovery:v1:";
export function recoveryScope(user: string, workspace: string, kind: string, document: string): string {
  return `${recoveryPrefix}${encodeURIComponent(user)}:${encodeURIComponent(workspace)}:${kind}:${encodeURIComponent(document)}:`;
}
function isForm(value: unknown): value is DraftForm {
  if (!value || typeof value !== "object") return false;
  const form = value as DraftForm;
  try {
    if (form.taxModelVersion === 1) parseDocumentTaxes(form.taxes);
    if (Array.isArray(form.lines)) for (const line of form.lines) parseRichDetails(line?.details);
  } catch { return false; }
  return [form.clientId,form.recipientContactId,form.number,form.issueDate,form.dueDate,form.note,form.reference,form.paymentInstructions,form.discountValue,form.discountLabel,form.defaultTaxRateId].every(value => typeof value === "string") &&
    typeof form.numberManuallyEdited === "boolean" && ["none","percentage","fixed_amount"].includes(form.discountKind) &&
    Array.isArray(form.lines) && form.lines.length <= 1000 && form.lines.every(line => line && typeof line.key === "string" && typeof line.description === "string" && typeof line.title === "string" && typeof line.contentEdited === "boolean" && ["inherit","none","override"].includes(line.taxMode) && typeof line.taxRateId === "string" && typeof line.quantity === "string" && typeof line.rate === "string");
}
export function readRecoveries(storage: Storage, scope: string): {key: string; value: DraftRecovery}[] {
  const results: {key: string; value: DraftRecovery}[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key?.startsWith(scope)) continue;
    try {
      const value = JSON.parse(storage.getItem(key) ?? "null") as DraftRecovery;
      if (value?.schema !== 1 || typeof value.editor !== "string" || !Number.isFinite(value.updatedAt) || !isForm(value.form) ||
        !(value.baseVersion === null || Number.isInteger(value.baseVersion)) || !(value.documentId === null || typeof value.documentId === "string")) continue;
      if (value.pending && (!/^[0-9a-f-]{36}$/i.test(value.pending.key) || !["POST","PATCH"].includes(value.pending.method) || typeof value.pending.body !== "string" || !isForm(value.pending.form))) continue;
      results.push({key,value});
    } catch { /* Ignore malformed local entries; never apply them to the server. */ }
  }
  return results.sort((a,b) => b.value.updatedAt - a.value.updatedAt);
}
export function writeRecovery(storage: Storage, scope: string, value: DraftRecovery): string {
  const key = scope + value.editor;
  storage.setItem(key, JSON.stringify(value));
  return key;
}
